import type { Role } from '@/types';

import type { UserFormValues, UserRow } from './types';

/** The viewer identity the row menu gates on — `id` (is-self) + `role` (hierarchy). Sourced from
 *  `useAuth().user` on the page (D5); kept minimal so the function stays pure and trivially testable.
 *  `role` is nullable because an unrecognised wire role degrades to `null` (see `roleSchema`); such
 *  a viewer is not a manager, so every row action is withheld. */
export type RowActionViewer = { id: string; role: Role | null };

export type RowActions = {
  showEllipsis: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canToggleStatus: boolean;
};

const MANAGER_ROLES: Role[] = ['super_admin', 'admin'];

/** Client-side row-menu gating (D5; ACs 4/7/30/31/37/38/42/43). No backend flag beyond `isLastInRole`.
 *  Delete + Disable are computed now for Phase 5; Phase 4 wires only Edit. */
export function rowActions(viewer: RowActionViewer, row: UserRow): RowActions {
  const showEllipsis =
    viewer.role !== null &&
    MANAGER_ROLES.includes(viewer.role) &&
    row.id !== viewer.id &&
    row.role !== 'super_admin' &&
    !(viewer.role === 'admin' && row.role === 'admin');

  return {
    showEllipsis,
    canEdit: showEllipsis,
    canDelete: showEllipsis && !row.isLastInRole,
    // Enable/Disable move between Active and Inactive only; an Activation Pending user has neither
    // (BE 409 USER_PENDING_ACTIVATION). Last-in-role blocks Disable (active) only (AC 43 vs 45).
    canToggleStatus:
      showEllipsis &&
      row.status !== 'pending_activation' &&
      !(row.isLastInRole && row.status === 'active'),
  };
}

/** Normalize the way zod does (names trimmed + inner whitespace collapsed, email lower-cased) so a
 *  trim-only / case-only edit reads as no change. */
const normalize = (v: UserFormValues): UserFormValues => ({
  firstName: v.firstName.trim().replace(/\s+/g, ' '),
  lastName: v.lastName.trim().replace(/\s+/g, ' '),
  phone: v.phone,
  email: v.email.trim().toLowerCase(),
  role: v.role,
});

/** Edit no-op short-circuit (D13): the changed-fields subset, or `null` when nothing genuinely changed.
 *  The Edit submit sends only this subset (PATCH), and fires no request/toast when it is `null`. */
export function diffUserForm(
  original: UserFormValues,
  current: UserFormValues,
): Partial<UserFormValues> | null {
  const o = normalize(original);
  const c = normalize(current);
  const changed: Partial<UserFormValues> = {};
  (Object.keys(c) as (keyof UserFormValues)[]).forEach((key) => {
    if (o[key] !== c[key]) changed[key] = c[key] as never;
  });
  return Object.keys(changed).length > 0 ? changed : null;
}
